/**
 * The shape of the data, and the small pure helpers that work on it.
 *
 * Keeping these free of storage and DOM concerns makes the rules (what counts
 * as billable, what an open waiting-on item is) easy to read in one place.
 */

import { daysBetween, today } from './dates.js';

export const DAY_TYPES = ['Full', 'Half', 'Non-billable', 'Not working'];
export const LOCATIONS = ['Bridgend', 'Remote', 'Travel', 'Other'];

/** Billable day equivalent for each day type. A half day counts as 0.5. */
export const DAY_TYPE_VALUE = {
  Full: 1,
  Half: 0.5,
  'Non-billable': 0,
  'Not working': 0,
};

/** Day types that appear on the client timesheet. */
export const BILLABLE_DAY_TYPES = ['Full', 'Half'];

export const DEFAULT_WORKSTREAMS = [
  'WS-01 US service and warehousing',
  'WS-02 Cost to serve and landed cost',
  'WS-03 Freight and warehousing network',
  'WS-04 Supply chain planning and control',
  'Admin',
];

export const DEFAULT_EXPENSE_CATEGORIES = ['Hotel', 'Fuel', 'Sustenance', 'Parking', 'Other'];

/** Short, collision-resistant ids for list entries. */
export function uid() {
  return (
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 8)
  );
}

export function defaultSettings() {
  return {
    id: 'settings',
    consultantName: '',
    companyName: '',
    companyNumber: '',
    clientName: '',
    clientContact: '',
    defaultLocation: 'Bridgend',
    workstreams: [...DEFAULT_WORKSTREAMS],
    expenseCategories: [...DEFAULT_EXPENSE_CATEGORIES],
  };
}

/** Fill in anything missing, so an older or hand-edited record still loads. */
export function normaliseSettings(raw) {
  const base = defaultSettings();
  if (!raw || typeof raw !== 'object') return base;
  return {
    ...base,
    ...raw,
    id: 'settings',
    workstreams: Array.isArray(raw.workstreams) && raw.workstreams.length ? raw.workstreams.slice() : base.workstreams,
    expenseCategories:
      Array.isArray(raw.expenseCategories) && raw.expenseCategories.length
        ? raw.expenseCategories.slice()
        : base.expenseCategories,
  };
}

export function emptyDay(date, defaultLocation = 'Bridgend') {
  return {
    date,
    dayType: 'Full',
    location: defaultLocation,
    locationOther: '',
    done: [],
    planned: [],
    waitingOn: [],
    expenses: [],
    notes: '',
    updatedAt: new Date().toISOString(),
  };
}

/** Coerce a stored day into the current shape; tolerates partial records. */
export function normaliseDay(raw, defaultLocation = 'Bridgend') {
  if (!raw || typeof raw !== 'object' || !raw.date) return null;
  const base = emptyDay(raw.date, defaultLocation);
  const list = (value) => (Array.isArray(value) ? value : []);
  return {
    ...base,
    ...raw,
    dayType: DAY_TYPES.includes(raw.dayType) ? raw.dayType : base.dayType,
    location: typeof raw.location === 'string' && raw.location ? raw.location : base.location,
    locationOther: typeof raw.locationOther === 'string' ? raw.locationOther : '',
    notes: typeof raw.notes === 'string' ? raw.notes : '',
    done: list(raw.done).map((e) => ({ id: e.id || uid(), text: e.text || '', workstream: e.workstream || '' })),
    planned: list(raw.planned).map((e) => ({
      id: e.id || uid(),
      text: e.text || '',
      workstream: e.workstream || '',
      done: Boolean(e.done),
      carried: Boolean(e.carried),
    })),
    waitingOn: list(raw.waitingOn).map((w) => ({
      id: w.id || uid(),
      who: w.who || '',
      what: w.what || '',
      sinceDate: w.sinceDate || raw.date,
      clearedDate: w.clearedDate || '',
    })),
    expenses: list(raw.expenses).map((x) => ({
      id: x.id || uid(),
      category: x.category || '',
      amount: Number.isFinite(Number(x.amount)) ? Number(x.amount) : 0,
      km: x.km === '' || x.km === null || x.km === undefined ? '' : Number(x.km),
      note: x.note || '',
      receiptRef: x.receiptRef || '',
    })),
  };
}

/** Strip blank rows so placeholder lines never reach storage or a report. */
export function cleanDay(day) {
  return {
    ...day,
    done: day.done.filter((e) => e.text.trim()),
    planned: day.planned.filter((e) => e.text.trim()),
    waitingOn: day.waitingOn.filter((w) => w.who.trim() || w.what.trim()),
    expenses: day.expenses.filter((x) => x.category || Number(x.amount) || x.note.trim() || x.receiptRef.trim()),
    updatedAt: new Date().toISOString(),
  };
}

/** True when a day record holds nothing worth keeping. */
export function isDayEmpty(day) {
  const clean = cleanDay(day);
  return (
    !clean.done.length &&
    !clean.planned.length &&
    !clean.waitingOn.length &&
    !clean.expenses.length &&
    !clean.notes.trim() &&
    clean.dayType === 'Full' &&
    !clean.locationOther.trim()
  );
}

export function billableValue(day) {
  return DAY_TYPE_VALUE[day.dayType] ?? 0;
}

export function isBillable(day) {
  return BILLABLE_DAY_TYPES.includes(day.dayType);
}

/** Location as it should read on a report, honouring the free-text "Other". */
export function locationLabel(day) {
  if (day.location === 'Other' && day.locationOther.trim()) return day.locationOther.trim();
  return day.location;
}

/** The distinct workstreams a day has done-work against, in entry order. */
export function workstreamsForDay(day) {
  const seen = [];
  for (const entry of day.done) {
    const ws = entry.workstream || 'Unassigned';
    if (entry.text.trim() && !seen.includes(ws)) seen.push(ws);
  }
  return seen;
}

/** One-line summary of a day's work, for the timesheet table and CSV. */
export function doneSummary(day, separator = '; ') {
  return day.done
    .map((e) => e.text.trim())
    .filter(Boolean)
    .join(separator);
}

/**
 * Waiting-on items that are still open across a set of days.
 *
 * An item is copied forward onto each new day, keeping its id and start date, so
 * the same item appears on many day records. The most recent copy is the
 * authoritative one: if that copy has been cleared, the item is closed.
 */
export function openWaitingOn(days) {
  const latest = new Map();
  for (const day of [...days].sort((a, b) => a.date.localeCompare(b.date))) {
    for (const item of day.waitingOn) {
      if (!item.who.trim() && !item.what.trim()) continue;
      latest.set(item.id, item);
    }
  }
  return [...latest.values()]
    .filter((item) => !item.clearedDate)
    .sort((a, b) => a.sinceDate.localeCompare(b.sinceDate));
}

/** How many days a waiting-on item has been open, as at `reference`. */
export function daysOpen(item, reference = today()) {
  return Math.max(0, daysBetween(item.sinceDate, item.clearedDate || reference));
}

/** Format pounds for display: £1,234.50 */
export function formatMoney(amount) {
  const value = Number(amount) || 0;
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
}

/** Format a day equivalent: 1, 0.5, 12.5 — never trailing zeroes. */
export function formatDays(value) {
  return String(Math.round(Number(value) * 100) / 100);
}

export function formatKm(value) {
  const n = Number(value) || 0;
  return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(n)} km`;
}
